<script setup lang="ts">
import { FormKitLazyProvider } from '@formkit/vue'
import { debounce } from "lodash"
import { FkSliderProps, FkSliderValue } from "~/components/FkSlider.props"

const { t } = useI18n()

const props = withDefaults(defineProps<FkSliderProps>(), {
  modelValue: undefined,
  step: 1,
  gap: 1,
  currency: "€"
})
const emit =
  defineEmits<{
    (e: "update:modelValue", value: number | FkSliderValue): void
  }>()

const minRef = ref(props.modelValue?.min)

const maxRef = ref(props.modelValue?.max)

const sliderValue = computed({
  get() {
    return [minRef.value, maxRef.value]
  },
  set(value) {
    minRef.value = value[0]
    maxRef.value = value[1]
  }
})

const minLabelStr = computed(() => tooltipFormat(minRef.value))

const maxLabelStr = computed(() => tooltipFormat(maxRef.value))

const tooltipFormat = (tooltipValue: string) => {
  return t("filters.slider.tooltip", { tooltipValue, currency: props.currency })
}

const inputAttrs = {
  sectionsSchema: {
    prefix: {
      $el: "span",
      children: props.currency
    }
  }
}

const updateModelValue = (value: number[]) => {
  if (value[0] === value[1]) {
    switch (true) {
      case value[1] === props.min:
        value[1] = value[1] + 1
        break
      case value[0] === props.max:
        value[0] = value[0] - 1
        break

      default:
        props.modelValue.max === value[1]
          ? (value[1] = value[1] + 1)
          : (value[0] = value[0] - 1)
        break
    }
  }

  minRef.value = value[0]
  maxRef.value = value[1]
  emit("update:modelValue", {
    min: minRef.value,
    max: maxRef.value
  })
}

watch(
  () => props.modelValue,
  (newModelValue) => {
    if (newModelValue.min !== minRef.value) {
      minRef.value = newModelValue.min
    }
    if (newModelValue.max !== maxRef.value) {
      maxRef.value = newModelValue.max
    }
  }
)

const debounceFunction = debounce((value) => updateModelValue(value), 300)

const setSlider = (value: number) => {
  debounceFunction(value)
}
</script>

<template>
  
<FormKitLazyProvider config-file="true">
<div class="flex justify-between px-0">
    <p class="beaver">
      <span class="font-bold">{{
        $t("filters.slider.minInputAttrsLabel")
      }}</span>
      {{ minLabelStr }}
    </p>
    <p class="beaver">
      <span class="font-bold">{{
        $t("filters.slider.maxInputAttrsLabel")
      }}</span>
      {{ maxLabelStr }}
    </p>
  </div>
  <FormKit type="form" :actions="false">
    <!-- 
      show-input attribute is needed to move slider pins in case of equal values 
      switch tooltip attribute to true to show toltip on top the pins
    -->
    <FormKit
      outer-class="w-full price-slider"
      type="slider"
      v-model="sliderValue"
      :max="max"
      :min="min"
      :tooltip-format="(value) => tooltipFormat(value)"
      tooltip="false"
      :input-attrs="inputAttrs"
      :min-input-attrs="{ label: $t('filters.slider.minInputAttrsLabel') }"
      :max-input-attrs="{ label: $t('filters.slider.maxInputAttrsLabel') }"
      show-input
      @input="setSlider"
    >
    </FormKit>
  </FormKit>
</FormKitLazyProvider>

</template>

<style lang="scss" scoped>
.formkit-form {
  display: flex;
  justify-content: center;
  align-items: center;
  :deep(.price-slider) {
    padding: 0.5rem 1rem;
    padding-bottom: 0rem;
    max-width: 100%;
    .formkit-linkedValues {
      display: none;
      //Uncomment if you want to show inputs (change display value)
      /*
      justify-content: space-between;
      margin-top: 1rem;
      margin-left: -0.5rem;
      margin-right: -0.5rem;
      .formkit-wrapper {
        width: 100%;
        > label {
          margin-bottom: 0rem;
        }
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        .formkit-inner {
          @apply ml-1 rounded px-2 py-1;
          display: flex;
          width: 100%;
          border: 1px solid rgb(234 234 234 / 1);
          input {
            width: 100%;
            padding: 0;
            border: 0;
          }
        }
      }
      */
    }
    .formkit-wrapper {
      > label {
        margin-bottom: 2rem;
        font-weight: normal;
      }
      .formkit-trackWrapper {
        @apply bg-grey-200;
        .formkit-fill {
          @apply bg-green-300;
        }
        .formkit-tooltip {
          @apply text-black-80;
        }
      }
      li {
        @apply border-white bg-green-300 pointer-events-auto h-6 w-6 cursor-pointer
         appearance-none rounded-full border-2 border-solid shadow-[0px_2px_8px_rgba(0,_0,_0,_0.32)];
      }
    }
  }
}
</style>
